import type { FeatureFlagKey } from '@hero/core';
import type { AppId, AppStatus } from '@hero/schema';
import { ALINMA_APP } from './alinma';
import { ALRAJHI_BANK_APP } from './alrajhi-bank';
import { CUSTOM_APP } from './custom-app';
import { DAFTRA_APP } from './daftra';
import { LOGISTI_APP } from './logisti';
import { ORACLE_APP } from './oracle';
import { QOYOD_APP } from './qoyod';
import { SAP_APP } from './sap';
import { SNB_APP } from './snb';
import { WHATSAPP_APP } from './whatsapp';
import type { AppDefinition } from './types';

/**
 * The marketplace catalog, in display order.
 *
 * Live apps first, then the ones that only exist to gauge demand. Both the
 * backend catalog endpoint and the dashboard read this same array, so a card
 * and the code behind it can never disagree.
 */
export const ALL_APPS: AppDefinition[] = [
  LOGISTI_APP,
  ALRAJHI_BANK_APP,
  SNB_APP,
  ALINMA_APP,
  WHATSAPP_APP,
  DAFTRA_APP,
  QOYOD_APP,
  SAP_APP,
  ORACLE_APP,
  CUSTOM_APP,
];

const BY_ID = new Map<AppId, AppDefinition>(ALL_APPS.map(app => [app.id, app]));

/** The definition for one app, or `undefined` for an id with no folder. */
export const getAppDefinition = (appId: AppId): AppDefinition | undefined => BY_ID.get(appId);

/** Whether the install endpoint should accept credentials for this lifecycle. */
export const isAppInstallableStatus = (status: AppStatus): boolean =>
  status === 'active' || status === 'beta';

/** Whether Hero has a working connector exposed for this app right now. */
export const isAppInstallable = (appId: AppId): boolean => {
  const definition = BY_ID.get(appId);
  return definition ? isAppInstallableStatus(definition.status) : false;
};

/** Apps with a live connector — what the install flow will accept. */
export const INSTALLABLE_APPS: AppDefinition[] = ALL_APPS.filter(app =>
  isAppInstallableStatus(app.status),
);

/** Distinct PostHog keys referenced by catalog apps — for frontend rollout hooks. */
export const APP_CATALOG_FEATURE_FLAGS = [
  ...new Set(
    ALL_APPS.map(app => app.featureFlag).filter((flag): flag is FeatureFlagKey => flag != null),
  ),
];

/**
 * Whether a connector is live for end users — installable lifecycle plus any rollout flag.
 */
export const isAppConnectorLive = (
  app: Pick<AppDefinition, 'status' | 'featureFlag'>,
  isFlagEnabled: (key: FeatureFlagKey) => boolean,
): boolean => {
  if (!isAppInstallableStatus(app.status)) return false;
  if (!app.featureFlag) return true;
  return isFlagEnabled(app.featureFlag);
};

/**
 * Whether a catalog card should appear in the marketplace at all.
 */
export const isAppCatalogVisible = (
  app: Pick<AppDefinition, 'status' | 'featureFlag'>,
  isFlagEnabled: (key: FeatureFlagKey) => boolean,
): boolean => {
  if (app.status === 'inactive') return false;
  if (!app.featureFlag) return true;
  return isFlagEnabled(app.featureFlag);
};
